import { selectedPoolAtom } from "@/atoms/shared"
import Button from "@/components/base/Button"
import { ButtonConnect } from "@/components/buttonConnect"
import { RelendModal } from "@/components/relendModal"
import { RelendTable } from "@/components/relendTable"
import { Stat } from "@/components/stat"
import useIsMobile from "@/hooks/useMobileDevice"
import useTrans from "@/hooks/useTrans"
import { SKELETON_VALUE } from "@/utils/constants"
import { TokenSymbol, tokenDecimalDigits } from "@/utils/helpers"
import { LOGO_SIZES, getTokenLogoDisplay } from "@/utils/logo"
import { formatToken, formatUsd } from "@/utils/numberFormatter"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import { Box, Container, Stack, TableCell, TableRow, Typography } from "@mui/material"
import { PublicKey } from "@solana/web3.js"
import BigNumber from "bignumber.js"
import { useAtom } from "jotai"
import React, { useMemo } from "react"
import * as styles from "./styles"
import { LiquidationTxResult } from "./txResult"
import { IViewProps } from "./types"

const View = ({
  liquidations,
  doesHaveItems,
  handleCloseModal,
  walletConnectButtonRef,
  isModalOpen,
  transactionDetail,
  hasNextPage,
  isFetchingNextPage,
  isLoading,
  loading,
  fetchNextPage,
  handleLiquidateObligation,
}: IViewProps) => {
  const trans = useTrans()
  const isMobileDevice = useIsMobile()
  const [selectedPool] = useAtom(selectedPoolAtom)

  const headers = useMemo(
    () => (
      <>
        <TableCell>{trans.liquidations.table.position_id}</TableCell>
        <TableCell>{trans.liquidations.table.loan_token}</TableCell>
        <TableCell>{trans.liquidations.table.collateral_token}</TableCell>
        <TableCell>{trans.liquidations.table.collateral_amount}</TableCell>
        <TableCell>{trans.liquidations.table.need_to_pay}</TableCell>
        <TableCell>{trans.liquidations.table.actions}</TableCell>
      </>
    ),
    [trans],
  )

  const items =
    liquidations &&
    liquidations.pages
      .map((group, i) => {
        return (
          <React.Fragment key={i}>
            {group.liquidations.map((item, index) => {
              const truncatedPositionId = item.positionId.slice(0, 8) + "..." + item.positionId.slice(-8)
              const selectedDepositReserve = selectedPool?.reserves.find((r) => r.address === item.depositReserve)
              const selectedBorrowReserve = selectedPool?.reserves.find((r) => r.address === item.borrowReserve)

              const depositSymbol = selectedDepositReserve?.symbol ?? "Token"
              const depositName = selectedDepositReserve?.name
              const borrowSymbol = selectedBorrowReserve?.symbol ?? "Token"
              const borrowName = selectedBorrowReserve?.name
              const depositReserveLogoDisplay = getTokenLogoDisplay(
                depositSymbol,
                selectedDepositReserve,
                LOGO_SIZES.SM,
              )
              const borrowReserveLogoDisplay = getTokenLogoDisplay(borrowSymbol, selectedBorrowReserve, LOGO_SIZES.SM)

              // amount = token; value = token in usd
              const needToPayAmount = selectedBorrowReserve
                ? new BigNumber(item.needToPayValue).div(selectedBorrowReserve.price)
                : BigNumber(0)
              const collateralAmount = selectedDepositReserve
                ? new BigNumber(item.depositAmount)
                    .shiftedBy(-selectedDepositReserve.decimals)
                    .times(selectedDepositReserve.cTokenExchangeRate)
                : new BigNumber(item.depositAmount)
              const collateralValue =
                !collateralAmount.isZero() && selectedDepositReserve
                  ? new BigNumber(collateralAmount).multipliedBy(selectedDepositReserve.price)
                  : item.depositedValue

              const collateralAmountDisplay = selectedDepositReserve
                ? formatToken(
                    collateralAmount,
                    tokenDecimalDigits[depositName as TokenSymbol],
                    false,
                    false,
                    selectedDepositReserve?.decimals === 0 ? BigNumber.ROUND_UP : undefined,
                    false,
                    styles.textCenter,
                  )
                : SKELETON_VALUE

              const needToPayAmountDisplay = selectedBorrowReserve
                ? formatToken(
                    needToPayAmount,
                    tokenDecimalDigits[borrowName as TokenSymbol],
                    false,
                    false,
                    selectedDepositReserve?.decimals === 0 ? BigNumber.ROUND_UP : undefined,
                    false,
                    styles.normalText,
                  )
                : SKELETON_VALUE

              const collateralValueDisplay = selectedDepositReserve ? `~${formatUsd(collateralValue)}` : SKELETON_VALUE

              const needToPayValueDisplay = selectedBorrowReserve
                ? `~${formatUsd(new BigNumber(item.needToPayValue))}`
                : SKELETON_VALUE

              if (isMobileDevice) {
                return (
                  <Stack
                    key={`position-${item.positionId}`}
                    className={`${index > 0 ? "border-t border-solid border-secondary mt-6 pt-6" : ""}`}
                    gap={1}
                  >
                    <Stack gap={0.5}>
                      <Stack
                        className='bg-[#F7F8F8] rounded p-3'
                        direction='row'
                        alignItems='center'
                        justifyContent='center'
                        gap={0.5}
                      >
                        <Box sx={styles.iconWrapper}>
                          {borrowReserveLogoDisplay}
                          <Typography className='text-xs text-neutral-900'>{borrowName}</Typography>
                        </Box>
                        <ArrowForwardIcon fontSize='inherit' className='text-neutral-600 text-xs' />
                        <Box sx={styles.iconWrapper}>
                          {depositReserveLogoDisplay}
                          <Typography className='text-xs text-neutral-900'>{depositName}</Typography>
                        </Box>
                      </Stack>
                      <Stat
                        statsWrapper={styles.statWrapper}
                        titleEl={
                          <Typography sx={styles.statTitleGray}>{trans.liquidations.table.position_id}</Typography>
                        }
                        content={<Typography className='text-neutral-900 text-xs'>{truncatedPositionId} </Typography>}
                        contentStyle={styles.boldCenterCell}
                      />
                      <Stat
                        statsWrapper={styles.statWrapper}
                        titleEl={
                          <Typography sx={styles.statTitleGray}>
                            {trans.liquidations.table.collateral_amount}
                          </Typography>
                        }
                        content={
                          <Stack>
                            <Typography className='text-neutral-900 text-xs font-semibold'>
                              {collateralAmountDisplay}{" "}
                              <span className='text-right text-neutral-500 text-xs'>{depositName}</span>
                            </Typography>
                            <Typography className='text-right text-neutral-500 text-xs'>
                              {collateralValueDisplay}
                            </Typography>
                          </Stack>
                        }
                        contentStyle={styles.boldCenterCell}
                      />
                      <Stat
                        statsWrapper={styles.statWrapper}
                        titleEl={
                          <Typography sx={styles.statTitleGray}>{trans.liquidations.table.need_to_pay}</Typography>
                        }
                        content={
                          <Stack>
                            <Typography className='text-neutral-900 text-xs font-semibold'>
                              {needToPayAmountDisplay}{" "}
                              <span className='text-right text-neutral-500 text-xs'>{borrowName}</span>
                            </Typography>
                            <Typography className='text-right text-neutral-500 text-xs'>
                              {needToPayValueDisplay}
                            </Typography>
                          </Stack>
                        }
                        contentStyle={styles.boldCenterCell}
                      />
                    </Stack>
                    <Button
                      variant='primary'
                      size='md'
                      fullWidth
                      onClick={() => {
                        handleLiquidateObligation(
                          new PublicKey(item.positionId),
                          needToPayAmount,
                          new PublicKey(item.depositReserve),
                          new PublicKey(item.borrowReserve),
                          selectedBorrowReserve,
                        )
                      }}
                      loading={loading}
                    >
                      {trans.liquidations.table.liquidate}
                    </Button>
                  </Stack>
                )
              }

              return (
                <TableRow sx={styles.tableBodyRow} key={`position-${item.positionId}`}>
                  <TableCell>
                    <Typography className='text-neutral-900 text-sm'>{truncatedPositionId}</Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction='row' alignItems='center' gap={1}>
                      {borrowReserveLogoDisplay}
                      <Typography className='text-neutral-900 text-sm'>{borrowName}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction='row' alignItems='center' gap={1}>
                      {depositReserveLogoDisplay}
                      <Typography className='text-neutral-900 text-sm'>{depositName}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography className='text-neutral-900 font-semibold text-sm'>
                      {collateralAmountDisplay}{" "}
                      <span className='text-neutral-500 font-normal text-xs'>{depositName}</span>
                    </Typography>
                    <Typography className='text-neutral-500 text-xs'>{collateralValueDisplay}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography className='text-neutral-900 font-semibold text-sm'>
                      {needToPayAmountDisplay}{" "}
                      <span className='text-neutral-500 font-normal text-xs'>{borrowName}</span>
                    </Typography>
                    <Typography className='text-neutral-500 text-xs'>{needToPayValueDisplay}</Typography>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant='primary'
                      size='md'
                      onClick={() => {
                        handleLiquidateObligation(
                          new PublicKey(item.positionId),
                          needToPayAmount,
                          new PublicKey(item.depositReserve),
                          new PublicKey(item.borrowReserve),
                          selectedBorrowReserve,
                          selectedDepositReserve,
                        )
                      }}
                      loading={loading}
                    >
                      {trans.liquidations.table.liquidate}
                    </Button>
                  </TableCell>
                </TableRow>
              )
            })}
          </React.Fragment>
        )
      })
      .filter(Boolean)

  return (
    <Container maxWidth='xl' className='mt-10' data-testid='liquidation-container'>
      {!isMobileDevice && (
        <Stack className='bg-white py-10 px-6 border border-solid border-secondary rounded-lg' alignItems='center'>
          <Typography className='font-bold font-montserrat text-neutral-1000 text-[2.5rem]'>
            {trans.liquidations.liquidation_position}
          </Typography>
          {doesHaveItems && (
            <>
              <Typography className='text-base text-neutral-600'>
                {trans.liquidations.choose_position_liquidate}
              </Typography>

              <Stack className='mt-10 w-full'>
                <RelendTable loading={isLoading} headers={headers} items={items} />
              </Stack>
            </>
          )}

          {!doesHaveItems && (
            <Typography className='text-base text-neutral-600'>{trans.dashboard.table.no_liquidations}</Typography>
          )}

          <Box sx={styles.loadMoreWrapper}>
            {hasNextPage && (
              <Button
                variant='primary'
                size='md'
                onClick={() => {
                  fetchNextPage()
                }}
                disabled={!hasNextPage}
                loading={isLoading || isFetchingNextPage}
                data-testid='liquidation-btn-load-more'
              >
                {trans.referral.table.load_more}
              </Button>
            )}
          </Box>
        </Stack>
      )}

      {isMobileDevice && (
        <Stack className='bg-white py-6 px-3 border border-solid border-secondary rounded-lg' alignItems='center'>
          <Typography className='font-montserrat font-bold text-2xl text-neutral-1000'>
            {trans.liquidations.liquidation_position}
          </Typography>
          <Typography className='text-sm text-neutral-600'>{trans.liquidations.choose_position_liquidate}</Typography>
          <Box className='mt-10'>{items}</Box>
          <Box sx={styles.loadMoreWrapper}>
            {hasNextPage && (
              <Button
                className='mt-5'
                variant='primary'
                size='md'
                onClick={() => {
                  fetchNextPage()
                }}
                disabled={!hasNextPage}
                sx={styles.loadMoreButton}
                loading={isLoading || isFetchingNextPage}
                data-testid='liquidation-btn-load-more'
              >
                {trans.referral.table.load_more}
              </Button>
            )}
          </Box>
        </Stack>
      )}

      <RelendModal
        open={isModalOpen}
        handleClose={handleCloseModal}
        isSmallPopup
        title={trans.dashboard.modal.confirm}
        showCloseButton
        data-testid='liquidate-modal'
      >
        <LiquidationTxResult transactionDetail={transactionDetail} handleCloseModal={handleCloseModal} />
      </RelendModal>

      <Box display='none' ref={walletConnectButtonRef}>
        <ButtonConnect />
      </Box>
    </Container>
  )
}

export default View
